import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import {
  collection,
  getFirestore,
  onSnapshot,
  query,
  orderBy,
  where,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import {
  Briefcase,
  Clock,
  CheckCircle,
  Eye,
  ShieldX,
  Star,
  TrendingUp,
  AlertCircle,
  Filter,
  X,
  Search,
  Sparkles,
  Target,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import VerificationRequiredModal from "../../components/VerificationRequiredModal";
import { filterTestsBySkillMatch } from "../../utils/skillMatching";
import Tooltip from "../../components/Tooltip/JobSeekerTooltip";

interface HrTestDoc {
  id: string;
  jobTitle?: string;
  hrEmail?: string;
  hrId?: string;
  company?: string;
  duration?: number;
  questionCount?: number;
  testData?: any;
  createdAt?: any;
  matchScore?: number;
  matchedSkills?: string[];
  missingSkills?: string[];
  recommendations?: string[];
  // New fields for classification
  isTechnical?: boolean;
  testType?: string;
  domain?: string;
  tags?: string[];
  difficulty?: string;
  jobDescription?: string;
}

const AvailableTests: React.FC = () => {
  const { user } = useAuth();
  const { isVerified } = useVerificationStatus();
  const [tests, setTests] = useState<HrTestDoc[]>([]);
  const [completedTestIds, setCompletedTestIds] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userSkills, setUserSkills] = useState<any[]>([]);
  const [filteredTests, setFilteredTests] = useState<HrTestDoc[]>([]);
  const [showAllTests, setShowAllTests] = useState(false);
  const [hrCompanies, setHrCompanies] = useState<Map<string, string>>(
    new Map()
  );

  // New filter states
  const [recommendationsEnabled, setRecommendationsEnabled] = useState(true);
  const [userClassification, setUserClassification] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [allAvailableTags, setAllAvailableTags] = useState<string[]>([]);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // job description modal
  const [showJDModal, setShowJDModal] = useState(false);
  const [currentJD, setCurrentJD] = useState<string | null>(null);

  // Fetch user skills and classification from profile
  useEffect(() => {
    if (!user) return;

    const fetchUserProfile = async () => {
      try {
        const db = getFirestore(firebaseApp);

        // Fetch from userProfiles for skills and classification
        const profileDoc = await getDoc(doc(db, "userProfiles", user.id));

        // Also fetch from jobseekers for classification (in case it's only there)
        const jobseekerDoc = await getDoc(doc(db, "jobseekers", user.id));

        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const resumeSkills =
            profileData.resumeData?.professionalInfo?.skills ||
            profileData.resumeData?.skills ||
            [];

          // Convert to the format expected by skill matching
          const skills = resumeSkills.map((skill: string) => ({
            name: skill,
            verified: profileData.skillsVerified || false,
            score: profileData.verificationScore,
          }));

          setUserSkills(skills);

          // Set user classification
          const classification = {
            isTechnical: profileData.isTechnical,
            profileType: profileData.profileType,
            primaryDomain: profileData.primaryDomain,
          };
          setUserClassification(classification);
        }

        // Fallback to jobseekers collection if classification not in userProfiles
        if (jobseekerDoc.exists() && !userClassification) {
          const jobseekerData = jobseekerDoc.data();
          if (jobseekerData.isTechnical !== undefined) {
            setUserClassification({
              isTechnical: jobseekerData.isTechnical,
              profileType: jobseekerData.profileType,
              primaryDomain: jobseekerData.primaryDomain,
            });
          }
        }
      } catch (error) {
      }
    };

    fetchUserProfile();
  }, [user, isVerified]);

  // Fetch

  useEffect(() => {
    const db = getFirestore(firebaseApp);
    const q = query(collection(db, "hrTests"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, async (snap) => {
      // Filter out disabled tests - only show tests where isEnabled is true (or undefined for backwards compatibility)
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((test) => test.isEnabled !== false);

      // Fetch HR company names for all tests
      const hrIds = [...new Set(docs.map((test) => test.hrId).filter(Boolean))];
      const companiesMap = new Map<string, string>();

      const hrJobMap = new Map<string, string>();
      const jdDocSnap = await getDocs(collection(db, "hrJobs"));
      jdDocSnap.forEach((docSnap) => {
        const data = docSnap.data() as {
          hrId?: string;
          jobDescription?: string;
        };

        if (!data.hrId || !data.jobDescription) return;

        // if multiple docs exist for same hrId, last one wins
        hrJobMap.set(data.hrId, data.jobDescription);
      });

      for (const hrId of hrIds) {
        try {
          const hrDoc = await getDoc(doc(db, "recruiters", hrId));
          if (hrDoc.exists()) {
            const hrData = hrDoc.data();
            companiesMap.set(hrId, hrData.company || "Company Not Specified");
          }
        } catch (error) {
        }
      }

      // Extract all unique tags from tests
      const tagsSet = new Set<string>();
      docs.forEach((test) => {
        if (test.tags && Array.isArray(test.tags)) {
          test.tags.forEach((tag: string) => tagsSet.add(tag));
          test.company = companiesMap.get(test.hrId) || "Company Not Specified";
          test.jobDescription =
            hrJobMap.get(test.hrId) || "Job Description Not Specified";
        }
      });
      setAllAvailableTags(Array.from(tagsSet).sort());

      setHrCompanies(companiesMap);
      setTests(docs);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Comprehensive filtering logic
  useEffect(() => {
    if (tests.length === 0) {
      setFilteredTests([]);
      return;
    }

    setIsFilterLoading(true);

    // Apply filters
    let filtered = [...tests];

    // 1. Sort by technical/non-technical match (if recommendations are ON)
    // Note: We no longer FILTER out tests - we just prioritize matching ones
    // This ensures all tests are visible, but better matches appear first
    if (
      recommendationsEnabled &&
      userClassification &&
      userClassification.isTechnical !== undefined
    ) {
      filtered = filtered.sort((a, b) => {
        // Tests matching user's technical classification come first
        const aMatches = a.isTechnical === userClassification.isTechnical;
        const bMatches = b.isTechnical === userClassification.isTechnical;

        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0;
      });
    }

    // 2. Filter by selected tags (AND logic - test must have ALL selected tags)
    if (selectedTags.length > 0) {
      filtered = filtered.filter((test) => {
        if (!test.tags || test.tags.length === 0) return false;

        // Test must have ALL selected tags (case-insensitive)
        return selectedTags.every((selectedTag) =>
          test.tags!.some(
            (testTag) => testTag.toLowerCase() === selectedTag.toLowerCase()
          )
        );
      });
    }

    // 3. Apply skill matching (existing logic)
    if (userSkills.length > 0 && !showAllTests) {
      filtered = filterTestsBySkillMatch(userSkills, filtered);
    }

    setFilteredTests(filtered);
    setIsFilterLoading(false);
  }, [
    tests,
    userSkills,
    recommendationsEnabled,
    userClassification,
    selectedTags,
    showAllTests,
  ]);

  // Fetch user's completed tests
  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id)
    );

    const unsub = onSnapshot(q, (snap) => {
      const completedIds = new Set<string>();
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.testId) {
          completedIds.add(data.testId);
        }
      });
      setCompletedTestIds(completedIds);
    });

    return () => unsub();
  }, [user]);

  const handleStartTest = (testId: string) => {
    if (!isVerified) {
      setShowVerificationModal(true);
      return;
    }
    // If verified, navigate to test
    window.location.href = `/job-seeker/take-test/${testId}`;
  };

  const openJD = (jobDescription: string | null | undefined) => {
    if (!jobDescription) return; // optionally show toast/alert
    setCurrentJD(jobDescription);
    setShowJDModal(true);
  };

  const closeJD = () => {
    setShowJDModal(false);
    setCurrentJD(null);
  };

  if (loading) return <div className="text-center py-10">Loading tests...</div>;

  // Show verification required message if user is not verified
  if (!isVerified) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldX className="w-10 h-10 text-yellow-600" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Verify Your Skills to Unlock This Feature
          </h1>

          {/* Message */}
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            To ensure quality and maintain standards, only verified candidates
            can access and attend tests. Please complete your skills
            verification to unlock all available tests.
          </p>

          {/* Benefits */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
              <Star className="w-5 h-5 mr-2" />
              Why Verify Your Skills?
            </h3>
            <ul className="text-blue-800 space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Access all available job tests and opportunities</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Get a verified badge on your profile</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Stand out to employers with verified credentials</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Quick AI-generated assessment based on your resume</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/job-seeker/skills-validation"
              className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              <Star className="w-5 h-5 mr-2" />
              Verify Skills Now
            </Link>

            <Link
              to="/job-seeker/profile"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              View Profile
            </Link>
          </div>

          {/* Additional Note */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need 60% or higher score to get verified • Quick 20-question
              assessment
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for tag management
  const addTag = (tag: string) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setTagSearchTerm("");
  };

  const removeTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const filteredTagSuggestions = allAvailableTags
    .filter(
      (tag) =>
        tag.toLowerCase().includes(tagSearchTerm.toLowerCase()) &&
        !selectedTags.includes(tag)
    )
    .slice(0, 10);

  const displayTests = showAllTests ? tests : filteredTests;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Filters Toggle */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Available Tests</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Filter className="w-4 h-4 mr-2" />
          {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
          {/* Recommendation Toggle */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <div>
                <h3 className="font-semibold text-gray-900">
                  AI Recommendations
                </h3>
                <p className="text-sm text-gray-600">
                  {recommendationsEnabled ? (
                    userClassification?.isTechnical !== undefined ? (
                      <>
                        Showing{" "}
                        {userClassification.isTechnical
                          ? "Technical"
                          : "Non-Technical"}{" "}
                        tests for you
                      </>
                    ) : (
                      <>Upload resume to enable smart recommendations</>
                    )
                  ) : (
                    <>Showing all tests regardless of match</>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setRecommendationsEnabled(!recommendationsEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                recommendationsEnabled ? "bg-purple-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  recommendationsEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Tag-based Search */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-gray-900 flex items-center">
                <Search className="w-4 h-4 mr-2 text-gray-600" />
                Search by Tags
              </label>
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  Clear all tags
                </button>
              )}
            </div>

            {/* Selected Tags */}
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                {selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full flex items-center space-x-1"
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-blue-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Tag Search Input */}
            <div className="relative">
              <input
                type="text"
                value={tagSearchTerm}
                onChange={(e) => setTagSearchTerm(e.target.value)}
                placeholder="Search tags (e.g., React, Python, Marketing...)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />

              {/* Tag Suggestions Dropdown */}
              {tagSearchTerm && filteredTagSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredTagSuggestions.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => addTag(tag)}
                      className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors text-sm"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500">
              {allAvailableTags.length} tags available • Select multiple tags to
              narrow results
            </p>
          </div>

          {/* Filter Summary */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">
                Showing {isFilterLoading ? "..." : displayTests.length} of{" "}
                {tests.length} tests
              </span>
              {(selectedTags.length > 0 || !recommendationsEnabled) && (
                <button
                  onClick={() => {
                    setSelectedTags([]);
                    setRecommendationsEnabled(true);
                  }}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Take Practice Test Card */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center space-x-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Target className="w-7 h-7 text-green-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              Take Practice Test
            </h2>
            <p className="text-gray-600">
              Improve your skills and get ready for real assessments
            </p>
          </div>
          <Link
            to="/job-seeker/take-test/demo"
            className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center space-x-2"
          >
            <Zap className="w-4 h-4" />
            <span>Start Practice</span>
          </Link>
        </div>
      </div>

      {/* Skills-based filtering info */}
      {userSkills.length > 0 && !showAllTests && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Star className="w-4 h-4 text-blue-600" />
            <span className="text-blue-900 font-medium">
              Personalized Recommendations
            </span>
          </div>
          <p className="text-blue-800 text-sm">
            Tests are ranked based on your skills and profile.
            {` Showing ${filteredTests.length} available tests.`}
          </p>
        </div>
      )}

      {/* No skills warning */}
      {userSkills.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-yellow-900 font-medium">
              Upload Resume for Better Recommendations
            </span>
          </div>
          <p className="text-yellow-800 text-sm">
            Upload your resume to get personalized test recommendations based on
            your skills.
          </p>
        </div>
      )}

      {/* Loading Animation */}
      {isFilterLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-blue-900">Applying filters...</span>
          </div>
        </div>
      )}

      {displayTests.length === 0 && !isFilterLoading && (
        <div className="text-center py-20 bg-white border rounded-lg">
          <p>
            {tests.length === 0
              ? "No tests have been published yet. Please check back later."
              : selectedTags.length > 0
              ? "No tests match the selected tags. Try removing some filters."
              : "No tests match your current criteria. Try adjusting your filters or showing all tests."}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {displayTests.map((t) => {
          const isCompleted = completedTestIds.has(t.id);

          return (
            <div
              key={t.id}
              className={`bg-white p-6 border rounded-lg shadow-sm ${
                isCompleted ? "border-green-200 bg-green-50" : "border-gray-200"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t.jobTitle || "Untitled Test"}
                    </h3>

                    {isCompleted && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                      </span>
                    )}
                    {t.matchScore && t.matchScore >= 70 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Star className="w-3 h-3 mr-1" />
                        Great Match
                      </span>
                    )}
                  </div>
                  {/* NEW: company name on its own line, directly below title */}
                  {t.company && (
                    <p className="text-sm text-gray-600 mb-2">{t.company}</p>
                  )}
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                    {typeof t.duration === "number" && (
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" /> {t.duration} min
                      </span>
                    )}
                    {t.questionCount && <span>{t.questionCount} Qs</span>}
                    {t.matchScore && (
                      <span
                        className={`flex items-center font-medium ${
                          t.matchScore >= 70
                            ? "text-green-600"
                            : t.matchScore >= 50
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        {t.matchScore}% match
                      </span>
                    )}
                  </div>

                  {/* Matched Skills */}
                  {t.matchedSkills && t.matchedSkills.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">
                        Your matching skills:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {t.matchedSkills.slice(0, 4).map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                        {t.matchedSkills.length > 4 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{t.matchedSkills.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Missing Skills Warning */}
                  {t.missingSkills && t.missingSkills.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-red-600 mb-1">
                        Missing critical skills:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {t.missingSkills.slice(0, 3).map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                        {t.missingSkills.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{t.missingSkills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Test Tags */}
                  {t.tags && t.tags.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Tags:</p>
                      <div className="flex flex-wrap gap-1">
                        {t.tags.slice(0, 8).map((tag, index) => (
                          <span
                            key={index}
                            onClick={() => {
                              if (!selectedTags.includes(tag)) {
                                addTag(tag);
                                if (!showFilters) setShowFilters(true);
                              }
                            }}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full cursor-pointer hover:bg-blue-100 hover:text-blue-700 transition-colors"
                            title="Click to filter by this tag"
                          >
                            {tag}
                          </span>
                        ))}
                        {t.tags.length > 8 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{t.tags.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Test Type Badge */}
                  {t.isTechnical !== undefined && (
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          t.isTechnical
                            ? "bg-purple-100 text-purple-800"
                            : "bg-teal-100 text-teal-800"
                        }`}
                      >
                        {t.isTechnical ? "Technical" : "Non-Technical"}
                      </span>
                      {t.domain && (
                        <span className="text-xs text-gray-600">
                          • {t.domain}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-2">
                {/* New View JD button (always visible) */}
                <button
                  type="button"
                  onClick={() => openJD(t.jobDescription)} // implement this
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  View JD
                </button>
                {isCompleted ? (
                  <>
                    <Tooltip content="View your results for this test">
                      <Link
                        to="/job-seeker/results"
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Results
                      </Link>
                    </Tooltip>
                    <Tooltip content="You have already taken this test">
                      <div className="flex items-center px-4 py-2 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed">
                        Already Taken
                      </div>
                    </Tooltip>
                  </>
                ) : (
                  <Link
                    to={`/job-seeker/take-test/${t.id}`}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
                  >
                    Start Test
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Verification Required Modal */}
      <VerificationRequiredModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
      />
      {/* Job Description Modal */}
      {showJDModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeJD}
            aria-hidden="true"
          />

          {/* Modal content */}
          <div className="relative z-10 w-[90vw] max-w-3xl max-h-[80vh] bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold">Job Description</h3>
              <button
                onClick={closeJD}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[65vh]">
              {currentJD ? (
                <p className="whitespace-pre-line text-sm text-gray-800">
                  {currentJD}
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  No job description available.
                </p>
              )}
            </div>

            <div className="flex justify-end px-4 py-3 border-t">
              <button
                onClick={closeJD}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailableTests;
